import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Assessment, Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import s3Utils from '../utils/s3Utils';
import { decryptAppId, decryptText } from '../utils/encryption';
import previewReportService from '../services/previewReportService';


const MedicalPreviewReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [labDetailsState, setlabDetailsState] = useState([]);
  const [basicDetailsState, setBasicDetails] = useState([]);
  const [assessmentTypeid, setassessmentTypeid] = useState('');
  const [disciplineState, setDiscipline] = useState([]);
  const [scopeState, setScope] = useState([]);
  const [organisationDetailsState, setOrganisationDetails] = useState([]);
  const [equipmentDetailsState, setEquipmentDetails] = useState([]);
  const [referenceMaterialsState, setReferenceMaterials] = useState([]);
  const [observationsState, setObservations] = useState([]);
  const [nonAnalyticalInstrumentsState, setNonAnalyticalInstruments] = useState([]);
  const [form153PhantomsState, setForm153Phantoms] = useState([]);
  const [radioDetailsState, setRadioDetails] = useState([]);
  const [proficiencyTestingState, setProficiencyTesting] = useState([]);
  const [isObservationVisible, setIsObservationVisible] = useState(false);
  const [isAutoDownloading, setIsAutoDownloading] = useState(false);

  useEffect(() => {
    const encryptedAppId = getQueryParam("appId");
    const encryptedOrgId = getQueryParam("orgId");
    const encryptedObservation = getQueryParam("observation");

    console.log('🔍 PreviewReport - Encrypted parameters received:', {
      encryptedAppId,
      encryptedOrgId,
      encryptedObservation
    });

    if (encryptedAppId && encryptedOrgId && encryptedObservation) {
      try {
        // Decrypt the parameters
        const decryptedAppId = decryptAppId(encryptedAppId);
        const decryptedOrgId = decryptText(encryptedOrgId);
        const decryptedObservation = decryptText(encryptedObservation);

        console.log('🔓 PreviewReport - Decrypted parameters:', {
          appId: decryptedAppId,
          orgId: decryptedOrgId,
          observation: decryptedObservation
        });

        // Use decrypted parameters
        if (decryptedAppId && decryptedOrgId) {
          fetchForm153Data(decryptedAppId, decryptedOrgId);
        }
        setIsObservationVisible(decryptedObservation === "true");

        console.log('✅ PreviewReport - Parameters successfully decrypted and used');
      } catch (error) {
        console.error('❌ Error decrypting preview report parameters:', error);
        // Fallback to original behavior if decryption fails
        const appId = encryptedAppId;
        const orgId = encryptedOrgId;
        const observation = encryptedObservation;
        if (appId && orgId) {
          fetchForm153Data(appId, orgId);
        }
        setIsObservationVisible(observation === "true");
      }
    }
  }, [location.search]);

  // Handle auto-download functionality
  useEffect(() => {
    const autoDownload = getQueryParam("autoDownload");
    const fromSubmission = getQueryParam("fromSubmission");

    if (autoDownload === "true" && fromSubmission === "true" && !isAutoDownloading && labDetailsState.length > 0) {
      console.log('🚀 Auto-downloading PDF after form submission...');
      setIsAutoDownloading(true);

      // Small delay to ensure all data is loaded
      setTimeout(() => {
        handleAutoDownloadAndSave();
      }, 1000);
    }
  }, [labDetailsState, isAutoDownloading]);

  const getQueryParam = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  const fetchForm153Data = async (appId, orgId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/preview-report/${appId}/${orgId}`
      );

      if (response.data.success) {
        const {
          labDetails,
          basicDetails,
          discipline,
          locationDetails,
          scope,
          organisationDetails,
          equipmentDetails,
          referenceMaterials,
          observations,
          nonAnalyticalInstruments,
          form153Phantoms,
          form153KitsRadioisotopes,
          proficiencyTestingProviders
        } = response.data.data;

        setlabDetailsState(labDetails);
        setBasicDetails(basicDetails);
        setassessmentTypeid(basicDetails[0]?.AssessmentTypeId);
        setDiscipline(discipline);
        setScope(scope);
        setOrganisationDetails(organisationDetails);
        setEquipmentDetails(equipmentDetails);
        setReferenceMaterials(referenceMaterials);
        setObservations(observations);
        setNonAnalyticalInstruments(nonAnalyticalInstruments);
        setForm153Phantoms(form153Phantoms);
        setRadioDetails(form153KitsRadioisotopes);
        setProficiencyTesting(proficiencyTestingProviders);
        console.log('assessmentTypeId', basicDetails[0]?.AssessmentTypeId)
      } else {
        console.error("API Error:", response.data.message);
      }
    } catch (error) {
      console.error("Request Failed:", error.message);
    }
  };

  const getObservationBySection = (sectionName, sourceId) => {
    if (sourceId) {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase() &&
          item.sourceid === sourceId
      );
    } else {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase()
      );
    }
  };

  const getObservationBySubSection = (sectionName, subSectionName = "") => {
    return observationsState.find(
      (item) =>
        item.SectionName?.toLowerCase() === sectionName.toLowerCase() &&
        (subSectionName
          ? item.SubsectionName?.toLowerCase() === subSectionName.toLowerCase()
          : true)
    );
  };

  // Get observation text from the Observation field
  const getObservationText = (observationData) => {
    if (!observationData) return "";
    return observationData.Observation || "";
  };


  // Render observation cell content - displays the Observation text and status
  const renderObservationCell = (observationData) => {
    if (!observationData) return "No observation";

    const observation = observationData.Observation;
    const status = observationData.InformationStatus;
    const informationProvided = observationData.InformationProvided;

    // Check if observation text is "Document adequacy check completed" and status is Adequate
    const isAdequate = observation &&
      observation.trim().toLowerCase() === "document adequacy check completed" &&
      status && status.toLowerCase() === "adequate";

    // If status is Adequate, show green "Adequate"
    if (isAdequate) {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "green", fontWeight: "bold" }}>Adequate</span>
        </div>
      );
    }

    // If status is Inadequate, show InformationProvided in red
    if (status && status.toLowerCase() === "inadequate") {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "red", fontWeight: "bold" }}>
            {informationProvided || observation || "No information provided"}
          </span>
        </div>
      );
    }

    // Default: show observation in black
    return (
      <div style={{ textAlign: "left" }}>
        <span style={{ color: "black" }}>
          {observation || "No observation"}
        </span>
      </div>
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Auto-download PDF and save to database after form submission
  const handleAutoDownloadAndSave = async () => {
    console.log('🎯 Starting auto PDF generation and save process...');

    try {
      const encryptedappId = getQueryParam("appId");
      const encryptedorgId = getQueryParam("orgId");

      const appId = decryptAppId(encryptedappId);
      const orgId = decryptText(encryptedorgId);

      if (!appId || !orgId) {
        console.error('Missing appId or orgId for PDF generation');
        navigate('/main/dashboard');
        return;
      }

      console.log('📄 Auto-generating PDF...');

      // Generate PDF using the existing handleDownloadPDF logic but save to database
      const now = new Date();
      const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
      const timeStr = `${hour12}-${minutes}${ampm}`;
      const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

      // Create the same PDF as the download function but get it as blob
      const reportData = {
        labDetailsState,
        disciplineState,
        scopeState,
        organisationDetailsState,
        equipmentDetailsState,
        referenceMaterialsState,
        nonAnalyticalInstrumentsState,
        observationsState,
        form153PhantomsState,
        radioDetailsState,
        proficiencyTestingState
      };

      console.log('🔧 Generating PDF with comprehensive service...');

      // Use the preview report service to generate and save PDF
      const result = await previewReportService.generateAndSavePreviewReportPPPFFL(
        orgId,
        appId,
        3, // Medical lab type ID
        'PreviewReport',
        isObservationVisible
      );

      if (result.success) {
        console.log('✅ PDF generated and saved successfully!');
        console.log('📊 Result:', result);

        // Show success message and redirect to dashboard
        setTimeout(() => {
          console.log('🏠 Redirecting to dashboard...');
          navigate('/main/dashboard');
        }, 2000);
      } else {
        console.error('❌ Failed to generate and save PDF:', result.message);
        alert(`Failed to generate PDF: ${result.message}`);
        navigate('/main/dashboard');
      }

    } catch (error) {
      console.error('❌ Error in auto PDF generation and save:', error);
      alert(`Error generating PDF: ${error.message}`);
      navigate('/main/dashboard');
    }
  };

  // Get the first lab detail (assuming there's at least one)
  const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};


  const viewS3File = async (fileName) => {
    try {
      const url = await s3Utils.generateSignedUrlForFilename(fileName);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Error generating S3 URL:", error);
    }
  };

  return (
    Number(assessmentTypeid) === 11 ? (
      <Box sx={{ p: 3, mt: 7, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Organization Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
              minWidth: 1000
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Sr No.
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Discipline
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Personal Information
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Contact Information
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Work Information
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Observation
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {organisationDetailsState.length > 0 ? (
                organisationDetailsState.filter(staff => staff.CompetenceAssessmentFile).map((staff, idx) => (
                  <TableRow key={staff.Id}>
                    <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      {staff.DisciplineName || "-"}
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div><strong>Name:</strong> {staff.Name || "-"}</div>
                        <div><strong>Designation:</strong> {staff.Designation || "-"}</div>
                        <div><strong>Qualification:</strong> {staff.Qualification || "-"}</div>
                        <div><strong>Specialization:</strong> {staff.Specialization || "-"}</div>
                        <div><strong>Experience:</strong> {staff.RelevantExperience || "-"} years</div>
                        <div><strong>Training:</strong> {staff.RelevantTraining || "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div><strong>Landline:</strong> {staff.LandlineNo || "-"}</div>
                        <div><strong>Mobile:</strong> {staff.ContactNo || "-"}</div>
                        <div><strong>Email:</strong> {staff.EmailID || "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div><strong>Employee Type:</strong> {staff.EmployeeType || "-"}</div>
                        <div><strong>Engagement Type:</strong> {staff.typeofEngagement || "-"}</div>
                        <div><strong>Timing:</strong> {staff.Timming || "-"}</div>
                        <div><strong>Authorized Signatory:</strong> {staff.IsAuthorisedSignatory ? "Yes" : "No"}</div>
                        {staff.IsAuthorisedSignatory && staff.SignatorySign && (
                          <div><strong>Signatory File:</strong> <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(staff.SignatorySign);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.photo && (
                          <div><strong>Photo:</strong> <a
                            href={staff.photo}
                            target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.TrainingCertificateFile && (
                          <div><strong>Training Certificate File:</strong> <a
                            href={staff.TrainingCertificateFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.AppointmentLetterFile && (
                          <div><strong>Appointment Letter File:</strong> <a
                            href={staff.AppointmentLetterFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.BioDataFile && (
                          <div><strong>Bio Data File:</strong> <a
                            href={staff.BioDataFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.CompetenceAssessmentFile && (
                          <div><strong>Competence Assessment File:</strong> <a
                            href={staff.CompetenceAssessmentFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.ExperienceCertificateFile && (
                          <div><strong>Experience Certificate File:</strong> <a
                            href={staff.ExperienceCertificateFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.QualificationCertificateFile && (
                          <div><strong>Qualification Certificate File:</strong> <a
                            href={staff.QualificationCertificateFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                      </div>
                    </TableCell>
                    {isObservationVisible && (
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {renderObservationCell(getObservationBySection("Staff", staff.Id))}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                    No staff details specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    ) : (
      <Box sx={{ p: 3, mt: 7, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        {/* PDF-specific styles */}
        <style>{`
        @media print {
          #pdf-report-content {
            page-break-inside: avoid;
          }
          .page-break-before {
            page-break-before: always !important;
            break-before: always !important;
          }
          .page-break-after {
            page-break-after: always !important;
            break-after: always !important;
          }
          table {
            page-break-inside: auto !important;
          }
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
          }
          td {
            page-break-inside: avoid !important;
          }
          thead {
            display: table-header-group !important;
          }
          tfoot {
            display: table-footer-group !important;
          }
        }
        #pdf-report-content table {
          page-break-inside: auto;
        }
        #pdf-report-content tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        #pdf-report-content td {
          page-break-inside: avoid;
        }
      `}</style>
        {/* PDF Export Container */}
        {/* Download Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
        </Box>
        <div id="pdf-report-content">
          <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 1, borderRadius: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Logo and Header */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src="/main/images/v1_9old.png"
                  alt="NABL Logo"
                  style={{ height: 70, marginRight: 24 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h7"
                    sx={{ fontWeight: "bold", color: "primary.main", mb: 0.5 }}
                  >
                    National Accreditation Board for Testing and Calibration
                    Laboratories
                  </Typography>
                  <Typography
                    variant="h9"
                    sx={{ color: "text.secondary", fontWeight: "bold" }}
                  >
                    Preview Report
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Name of Laboratory:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.CabName || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Contact Person:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.NablContPerson || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Mobile:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.ContactPersonMobileNo || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Email:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.ContactPersonEmail1 || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  UserName:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(() => {
                      console.log('Current basicDetailsState:', basicDetailsState);
                      const loginName = Array.isArray(basicDetailsState) ? basicDetailsState[0]?.LoginName : basicDetailsState?.LoginName;
                      console.log('LoginName value:', loginName);
                      return loginName || "-";
                    })()}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  CAB ID:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(() => {
                      console.log('Current basicDetailsState:', basicDetailsState);
                      const ApplicationNo = Array.isArray(basicDetailsState) ? basicDetailsState[0]?.ApplicationNo : basicDetailsState?.ApplicationNo;
                      console.log('ApplicationNo value:', ApplicationNo);
                      return ApplicationNo || "-";
                    })()}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Standard:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    ISO 15189:{(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.standard : basicDetailsState?.standard) || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Lab Type:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.LabType : basicDetailsState?.LabType) || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Accreditation Category:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.Assessmentype : basicDetailsState?.Assessmentype) || "-"}
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          {/* Laboratory Details Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Lab General Details
          </Typography>

          {/* Subsection: Name of the CAB */}
          <TableContainer
            component={Paper}
            sx={{ mb: 2, maxWidth: "100%", mx: "auto", borderRadius: 0 }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="right" sx={{ width: "50%" }}>
                    <strong>Details</strong>
                  </TableCell>
                  <TableCell align="left" sx={{ width: "50%" }}>
                    <strong>Data Feeded by Lab</strong>
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ width: "30%" }}>
                      <strong>Observation</strong>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Name of the CAB
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CabName || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ width: "30%", fontSize: "0.82rem" }}
                      rowSpan={17}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySection("Laboratory Details", null))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Type of CAB
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CabType === "Diagnostic"
                      ? "Medical Testing Laboratory"
                      : labDetail.CabType === "Medical Imaging"
                        ? "Medical Imaging"
                        : labDetail.CabType === "Both"
                          ? "Medical Testing Laboratory + Medical Imaging"
                          : "-"
                    }
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    GSTIN
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.GSTIN || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    GST Exemption Certificate
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.GSTExemptionFile ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.GSTExemptionFile);
                        }}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    PAN Number of Operational Entity
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.PANTANNumber || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    TAN Number of Operational Entity
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.panTanNumberOperationalEntity || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    PAN Number of Supporting Entity
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.panTanNumberSupportingEntity || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    TAN Number of Supporting Entity
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.TanNumberSupportingEntity || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Address
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Address || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Country
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CountryName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    State/Province
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.StateName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    City
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CityName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    District
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.DistrictName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Sub District
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SubDistrictName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Village/Town
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.VillageTown || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Pin Code
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Pincode || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Mobile No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.MobileNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Landline No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.LandlineNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Fax
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Fax || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Fax ISD Code
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.FaxIsdCode || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Email || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Are you from SEZ region (Special economic zone)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.IsFromSEZ ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.IsFromSEZ && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        SEZ Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SEZCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.SEZCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether any individual or organization has provided
                    consultancy for preparing towards NABL Accreditation
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.HasConsultancy ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.HasConsultancy && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of Individual/Organization with city
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ConsultancyName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Registered Office Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ConsultancyAddress || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Has adverse action has been initiated/taken against the laboratory in the past?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.HasAdverseAction ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.HasAdverseAction === "Yes" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of adverse action
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.AdverseActionType || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Adverse Decision Date
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.AdverseDecisionDate ? formatDate(labDetail.AdverseDecisionDate) : "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Previous Lab Id from NABL
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.PreviousLabId || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Legal Identity Details */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Legal Identity Details
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Legal Identity Type
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.LegalIdentityType || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={8}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Legal Entity Type", "legal-entity-type"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.LegalIdentityType === "Proprietorship" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of the Owner
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OwnerName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Registration Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.MCIRegNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc1 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc1);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.StateRegistrationNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc2 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc2);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Partnership" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Partnership Deed
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CompaniesActCertificate ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.CompaniesActCertificate);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Identity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "No"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of the Owner
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OwnerName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Registration Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.MCIRegNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc1 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc1);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.StateRegistrationNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc2 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc2);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "LLP" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Companies Act Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LLPCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LLPCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Companies Act" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Companies Act Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CompaniesActCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.CompaniesActCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_State || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Pin Code
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CompaniesActPincode || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Public Ltd" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Public Ltd Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.PublicLtdCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.PublicLtdCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Registered Office Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "No"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Government" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Are you an Autonomous Body?
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.GovernmentIsAutonomous ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Gazette or Government Notification or self-Declaration on letter head by Head of the organization
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.GovernmentCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.GovernmentCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Trust" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Trust Certificate/Deed
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.TrustCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.TrustCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Society" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Society Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SocietyCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.SocietyCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Are you an Autonomous Body?
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SocietyIsAutonomous ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "One Person Company" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Registered Office Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        One Person Company Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OPCCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.OPCCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Type Of Functioning */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Type Of Functioning
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Type Of Functioning
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.TypeOfFunctioning || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={8}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Type of Functioning", "type-of-functioning"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OthersServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                {/* {labDetail.TypeOfFunctioning === "hospital-owned" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name Of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Upload Hospital Registration Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalRegistrationCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.HospitalRegistrationCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Address of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_State || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Pincode of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalPincode || "No"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.TypeOfFunctioning === "hospital-outsource" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name Of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLaboratoryName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Hospital and Laboratory MOU
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalLabMouFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.HospitalLabMouFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Laboratory Address of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLabAddress || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLabState || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Pincode of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLabPincode || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.TypeOfFunctioning === "cro-lab" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CroServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.TypeOfFunctioning === "others" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OthersServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )} */}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Contact Person for NABL */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Contact Person for NABL
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Contact Person for NABL
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.NablContPerson || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={6}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Contact Person from laboratory for NABL", "contact-person-nabl"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Designation
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SpocDesig || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Mobile No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonMobileNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Landline No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonTelephoneNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Fax
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonFaxNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Email 1
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonEmail1 || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Email 2
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonEmail2 || "-"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Category of Facility(s) Applied Under the Scope of Accreditation */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Category of Facility(s) Applied Under the Scope of Accreditation
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Permanant Facility
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.PermanentFacility ? "Yes" : "No"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={4}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Category of Facility", "category-of-facility"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Site Facility
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SiteFacility ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    POCT (Point of Care Testing)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.POCTFacility ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {/* {labDetail.CabType !== 'Medical Imaging' && (
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Category of which accreditation is being sought
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.AccreditationCategory || "-"}
                    </TableCell>
                  </TableRow>
                )} */}

                {/* <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Mobile Facility
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.MobileFacility ? "Yes" : "No"}
                </TableCell>
              </TableRow> */}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Parent Organisation Details */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Parent Organisation Details
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Name of the Parent Organization Applicable?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.IsParentOrgApplicable ? "Yes" : "No"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={labDetail.IsParentOrgApplicable ? 1 : 5}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Parent Organization", "parent-organization"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.IsParentOrgApplicable == true && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of the Parent Organization
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Contact No.
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgContactNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Landline No.
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgLandlineNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Fax No.
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgFaxNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Email Id
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgEmailId || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
           {/* Subsection: Registration number - CEA / STATE / DISTRICT Registry */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Registration number - CEA / STATE / DISTRICT Registry, that is
            relevant and linked to the laboratory, applying for accreditation
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Registration number - CEA / STATE / DISTRICT Registry, that is relevant and linked to the laboratory, applying for accreditation
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CEA_STATE_DISTRICT_Registry}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={labDetail.CEA_STATE_DISTRICT_Registry ? 1 : 5}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Registration number - CEA / STATE / DISTRICT Registry", "CEA-STATE-DISTRICT-Registry"))}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
            {/* Subsection:PAN/TAN of Supporting Entity */}
           {/* <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            PAN/TAN of Supporting Entity
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    PAN/TAN of Supporting Entity
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.panTanNumberSupportingEntity}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={labDetail.panTanNumberSupportingEntity ? 1 : 5}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("PAN/TAN of Supporting Entity", "pan-tan-supporting-entity"))}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer> */}


          {/* Subsection: Collection of Sample From */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Collection of Sample From
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Sample Collection Centre(s)/ Facility(ies)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SampleColletion ? "Yes" : "No"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={7}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Collection of Sample From", "sample-collection"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.SampleColletion && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        No of Collection Centres
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.NumberOfCollectionCenters || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Relevant Document
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SampleCollectionDocument ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.SampleCollectionDocument);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Home collection
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.HomeColletion ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.HomeColletion && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Relevant Document
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HomeCollectionDocument ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.HomeCollectionDocument);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Other Source(s)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.OtherSource ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.OtherSource && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Relevant Document
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OtherSourcesDocument ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.OtherSourcesDocument);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Internal Audit and Management Review */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Internal Audit and Management Review
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Start Date of Last Internal Audit
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.InternalAuditFromDate)}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={7}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Internal Audit and Management Review", "internal-audit-management-review"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    End Date of Last Internal Audit
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.InternalAuditToDate)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Internal Audit Document
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.InternalAuditDocument ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.InternalAuditDocument);
                        }}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether all requirements of ISO 15189:2022 covering all activities of laboratory have been audited at least once in last one year?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ISO15189Audited ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether various locations (including collection centers)
                    covered in the audit?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.LocationsAudited ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether Pre and post examination activities included in the
                    audit schedule?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.PrePostExamAudited ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Start Date of Last Management Review
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.ManagementReviewFromDate)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    End Date of Last Management Review
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.ManagementReviewToDate)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Management Review Document
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ManagementReviewDocument ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.ManagementReviewDocument);
                        }}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Subsection: Internal Audit and Management Review */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Organization Chart
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                {/* ROW 1 */}
                <TableRow>
                  <TableCell align="right" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    Organization Chart Document
                  </TableCell>

                  <TableCell align="left" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    {labDetail.OrganizationChart ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const fileurl = labDetail.OrganizationChart.split("/").pop();
                          viewS3File(fileurl);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {/* ✅ OBSERVATION CELL (RIGHT SIDE, ROWSPAN) */}
                  {isObservationVisible && (
                    <TableCell
                      align="left"
                      rowSpan={3}   // 👈 total rows in this table
                      sx={{
                        width: "20%",
                        fontSize: "0.82rem",
                        verticalAlign: "top",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {renderObservationCell(
                        getObservationBySubSection(
                          "Organization Details",
                          "organization-details"
                        )
                      )}
                    </TableCell>
                  )}
                </TableRow>

                {/* ROW 2 */}
                <TableRow>
                  <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                    Parent Organization Chart Document
                  </TableCell>
                  <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                    {labDetail.ParentOrganizationChart ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const fileurl =
                            labDetail.ParentOrganizationChart.split("/").pop();
                          viewS3File(fileurl);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>

                {/* ROW 3 */}
                <TableRow>
                  <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                    Management System Document
                  </TableCell>
                  <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                    {labDetail.ManagementSystemDocument ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const fileurl =
                            labDetail.ManagementSystemDocument.split("/").pop();
                          viewS3File(fileurl);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>


          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Proficiency Testing Documents
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    Proficiency Testing Documents
                  </TableCell>

                  <TableCell align="left" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    {labDetail.QMSFile ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.QMSFile);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {isObservationVisible && (
                    <TableCell
                      align="left"
                      rowSpan={1}
                      sx={{
                        width: "20%",
                        fontSize: "0.82rem",
                        verticalAlign: "top",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {renderObservationCell(
                        getObservationBySubSection(
                          "Proficiency Testing Document",
                          "enclosure"
                        )
                      )}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>



          {/* Section:  Lab Discipline Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Disciplines
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableHead sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                    <strong>Sr No.</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                    <strong>Discipline Name</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disciplineState.length > 0 ? (
                  disciplineState.map((discipline, index) => (
                    <TableRow key={discipline.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {discipline.DisciplineName}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>No disciplines specified</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Lab Location Details */}
          {/* <Typography variant="h6" sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}>
            Lab Location Details 
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}>
          <Table 
          size="small"
          sx={{
    border: "1px solid #bdbdbd",
    "& th, & td": { border: "1px solid #bdbdbd" },
    borderCollapse: "collapse",
  }}>
    <TableHead>
      <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
        <TableCell align="right" sx={{ width: "50%" }}><strong>Details</strong></TableCell>
        <TableCell align="left" sx={{ width: "50%" }}><strong>Data Feeded by Lab</strong></TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {locationDetailsState.length > 0 ? locationDetailsState.map((location, idx) => (
        <>
          <TableRow key={location.Id + "-name"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Location Name</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.Name || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-group"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Address</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.Address || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-standard"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>District</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.District || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-range"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>City</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.City || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-performed"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Pin Code</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.PinCode || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-contact"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Contact No</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.ContactNo || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-fax"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Fax No.</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.FaxNo || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-email"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>E-mail</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.Email || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-laboratory-size"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Laboratory size</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.LaboratorySize || "-"}</TableCell>
          </TableRow>
          {idx !== locationDetailsState.length - 1 && (
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }} />
            </TableRow>
          )}
        </>
      )) : (
        <TableRow>
          <TableCell align="center" colSpan={2}>No location details specified</TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
        </TableContainer> */}

          {/* Section: Scope of Testing */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Proposed Scope
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 900
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Scope Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Range Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Equipment Details
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {scopeState.length > 0 ? (
                  [...scopeState].sort((a, b) => {
                    const disciplineA = (a.DisciplineName || '').toLowerCase();
                    const disciplineB = (b.DisciplineName || '').toLowerCase();
                    return disciplineA.localeCompare(disciplineB);
                  }).map((scope, idx) => (
                    <TableRow key={scope.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {scope.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Test Code:</strong> {scope.TestCode || "-"}</div>
                          <div><strong>Materials/Products Tested:</strong> {scope.GroupTested || "-"}</div>
                          <div><strong>Specific Test Performed:</strong> {scope.TypeOfTest || "-"}</div>
                          <div><strong>Test Method/Equipment Used:</strong> {scope.ScopeStandard || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Type:</strong> {scope.rangeType || "-"}</div>
                          <div><strong>Facility:</strong> {scope.testPerformedAt || "-"}</div>
                          <div><strong>Range:</strong> {`${scope.RangeLL || '-'} ${scope.RangeLLUnit || '-'} To ${scope.RangeUL || '-'} ${scope.RangeULUnit || '-'}`}</div>
                          <div><strong>%CV / MU:</strong> {scope.cv ? `CV: ${scope.cv}` : ''} {scope.mu ? `MU: ${scope.mu}` : ''} {!scope.cv && !scope.mu ? '-' : ''}</div>
                          {scope.rangeRemark && (
                            <div><strong>Range Remarks:</strong> {scope.rangeRemark}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Equipment:</strong> {scope.Equipment || "-"}</div>
                          <div><strong>Reference Material:</strong> {scope.RefName || "-"}</div>
                          <div><strong>Proficiency Testing:</strong> {scope.PtName || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Scope", scope.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No scope details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Proficiency Testing Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Proficiency Testing Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 900
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    PT Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    ILC Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Remarks
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observations
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {proficiencyTestingState.length > 0 ? (
                  proficiencyTestingState.map((pt, index) => (
                    <TableRow key={pt.Id || index}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div>
                          <div><strong>Participation In:</strong> {pt.participationIn || "-"}</div>
                          <div><strong>Nodal Laboratory:</strong> {pt.nodalLaboratory || "-"}</div>
                          <div><strong>Other Details:</strong> {pt.nodalLaboratoryOthers || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div>
                          <div><strong>ILC Details:</strong> {pt.ilcDetails || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div>
                          <div><strong>Remarks:</strong> {pt.remarks || "-"}</div>
                          <div><strong>Created:</strong> {pt.CreatedOn ? new Date(pt.CreatedOn).toLocaleDateString() : "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Proficiency Testing", `${pt.Id}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No proficiency testing details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>


          {/* Section: Organization Staff Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Organization Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 1000
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Personal Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Contact Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Work Information
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {organisationDetailsState.length > 0 ? (
                  organisationDetailsState.map((staff, idx) => (
                    <TableRow key={staff.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {staff.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Name:</strong> {staff.Name || "-"}</div>
                          <div><strong>Designation:</strong> {staff.Designation || "-"}</div>
                          <div><strong>Qualification:</strong> {staff.Qualification || "-"}</div>
                          <div><strong>Specialization:</strong> {staff.Specialization || "-"}</div>
                          <div><strong>Experience:</strong> {staff.RelevantExperience || "-"} years</div>
                          <div><strong>Training:</strong> {staff.RelevantTraining || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Landline:</strong> {staff.LandlineNo || "-"}</div>
                          <div><strong>Mobile:</strong> {staff.ContactNo || "-"}</div>
                          <div><strong>Email:</strong> {staff.EmailID || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Employee Type:</strong> {staff.EmployeeType || "-"}</div>
                          <div><strong>Entity Type:</strong> {staff.supportingEntity || "-"}</div>
                          <div><strong>Engagement Type:</strong> {staff.typeofEngagement || "-"}</div>
                          <div><strong>Timing:</strong> {staff.Timming || "-"}</div>
                          <div><strong>Authorized Signatory:</strong> {staff.IsAuthorisedSignatory ? "Yes" : "No"}</div>
                          {staff.IsAuthorisedSignatory && staff.SignatorySign && (
                            <div><strong>Signatory File:</strong> <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                viewS3File(staff.SignatorySign);
                              }}
                              style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                            >
                              View
                            </a></div>
                          )}
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Staff", staff.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No staff details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Equipment Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Equipment Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 1100
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Equipment Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Technical Specifications
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 240, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Calibration Information
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {equipmentDetailsState.length > 0 ? (
                  equipmentDetailsState.map((equipment, idx) => (
                    <TableRow key={equipment.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {equipment.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Equipment Name:</strong> {equipment.NameOfEquipment || "-"}</div>
                          <div><strong>Equipment ID:</strong> {equipment.Equipmentid || "-"}</div>
                          <div><strong>UID:</strong> {equipment.Uid || "-"}</div>
                          <div><strong>Model:</strong> {equipment.Model || "-"}</div>
                          <div><strong>Serial No:</strong> {equipment.SerialNo || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Manufacturer:</strong> {equipment.MakeCompany || "-"}</div>
                          <div><strong>Year of Make:</strong> {equipment.YearOfMake || "-"}</div>
                          <div><strong>Receipt Date:</strong> {formatDate(equipment.ReceiptDate) || "-"}</div>
                          <div><strong>Installation Date:</strong> {formatDate(equipment.DateOfInstallation) || "-"}</div>
                          <div><strong>Limit of Detection:</strong> {equipment.LimitOfDetection || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Calibration Date:</strong> {formatDate(equipment.DateOfLastCalibration) || "-"}</div>
                          <div><strong>Calibration Due:</strong> {formatDate(equipment.CalibrationDueOn) || "-"}</div>
                          <div><strong>Calibrated By:</strong> {equipment.CalibratedByType || "-"}</div>
                          <div><strong>Calibration Agency:</strong> {equipment.CalibratedByType === "In-house" ? equipment.InHouseCalibration || "-" : equipment.ExternalCalibrationAgency || "-"}</div>
                          <div><strong>Traceability:</strong> {equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || "-"}</div>
                          <div><strong>Entity Type:</strong> {equipment.supportingEntity || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Equipment", `${equipment.Id}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No equipment details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Reference Materials */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Reference Material Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 900
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 300, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Material Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 250, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Source & Traceability
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {referenceMaterialsState.length > 0 ? (
                  referenceMaterialsState.map((material, idx) => (
                    <TableRow key={material.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {material.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Name:</strong> {material.Name || "-"}</div>
                          <div><strong>Type:</strong> Reference Material/Strain/Culture</div>
                          <div><strong>Date of Expiry:</strong> {formatDate(material.DateOfExpiry) === "01/01/1900" ? "-" : formatDate(material.DateOfExpiry)}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Source:</strong> {material.Source || "-"}</div>
                          <div><strong>Traceability:</strong> {material.Traceability || "-"}</div>
                          <div><strong>Entity Type:</strong> {material.supportingEntity || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Reference Material", material.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 4 : 3}>
                      No reference materials specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Non Analytical Instruments Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Non Analytical Instruments Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 800
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 250, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Instrument Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 300, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Calibration Information
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {nonAnalyticalInstrumentsState.length > 0 ? (
                  nonAnalyticalInstrumentsState.map((instrument, idx) => (
                    <TableRow key={instrument.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Instrument Name:</strong> {instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : "-"}</div>
                          <div><strong>UID:</strong> {instrument.UID || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Calibration Frequency:</strong> {instrument.Calibration_Frequency || "-"}</div>
                          <div><strong>Last Calibration Date:</strong> {formatDate(instrument.LastCalibration_Date) || "-"}</div>
                          <div><strong>Next Calibration Date:</strong> {formatDate(instrument.NextCalibration_Date) || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Non-Analytical Instruments", `${instrument.id}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 3 : 2}>
                      No Non Analytical Instruments Specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Phantoms Details */}
          {(labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
              >
                Phantoms Details
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
              >
                <Table
                  size="small"
                  sx={{
                    border: "1px solid #bdbdbd",
                    "& th, & td": { border: "1px solid #bdbdbd" },
                    borderCollapse: "collapse",
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                      <TableCell align="right" sx={{ width: "50%" }}>
                        <strong>Details</strong>
                      </TableCell>
                      <TableCell align="left" sx={{ width: "50%" }}>
                        <strong>Data Feeded by Lab</strong>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ width: "30%" }}>
                          <strong>Observation</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form153PhantomsState.length > 0 ? (
                      form153PhantomsState.map((phantom, idx) => (
                        <>
                          <TableRow key={phantom.Id + "-PhantomName"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Phantom Type
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.PhantomType || "-"}
                            </TableCell>
                            {isObservationVisible && (
                              <TableCell
                                align="center"
                                sx={{ fontSize: "0.82rem" }}
                                rowSpan={nonAnalyticalInstrumentsState.length}
                                style={{ verticalAlign: "top" }}
                              >
                                {renderObservationCell(getObservationBySection("phantoms", phantom.Id))}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow key={phantom.Id + "-Discipline"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Discipline
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.DisciplineName || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-typeoftest"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Type of Test
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.TypeOfTestNames || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-Specifications"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Specifications
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.Specifications || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-DateofPlacing"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Date of Placing
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {formatDate(phantom.DateofPlacing) || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-Traceability"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Traceability
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.Traceability || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-supportingEntity"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Entity Type
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.supportingEntity || "-"}
                            </TableCell>
                          </TableRow>

                          {idx !== form153PhantomsState.length - 1 && (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                              />
                            </TableRow>
                          )}
                        </>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={2}>
                          No Phantoms Specified
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {/* Section: Radioisotopes/Test Kits Details */}
          {(labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
              >
                Radioisotopes/Test Kits Details
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
              >
                <Table
                  size="small"
                  sx={{
                    border: "1px solid #bdbdbd",
                    "& th, & td": { border: "1px solid #bdbdbd" },
                    borderCollapse: "collapse",
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                      <TableCell align="right" sx={{ width: "50%" }}>
                        <strong>Details</strong>
                      </TableCell>
                      <TableCell align="left" sx={{ width: "50%" }}>
                        <strong>Data Feeded by Lab</strong>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ width: "30%" }}>
                          <strong>Observation</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {radioDetailsState.length > 0 ? (
                      radioDetailsState.map((radio, idx) => (
                        <>
                          <TableRow key={radio.Id + "-RadioName"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Name of Radioisotopes
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.NameofRadioisotopes || "-"}
                            </TableCell>
                            {isObservationVisible && (
                              <TableCell
                                align="center"
                                sx={{ fontSize: "0.82rem" }}
                                rowSpan={nonAnalyticalInstrumentsState.length}
                                style={{ verticalAlign: "top" }}
                              >
                                {renderObservationCell(getObservationBySection("radioisotopes", radio.Id))}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow key={radio.Id + "-Discipline"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Discipline
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.DisciplineName || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-typeoftest"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Type of Test
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.TypeOfTestNames || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-HalfLife"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Half Life
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.HalfLife || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-Energy"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Energy (KeV)
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.EnergyKeV || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-MaxActivityPermitted"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Max Activity Permitted
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.MaxactivityPermitted || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-DetailsOfSupplier"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Details of Supplier
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.DetailsofSupplier || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-supportingEntity"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Entity Type
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.supportingEntity || "-"}
                            </TableCell>
                          </TableRow>
                          {idx !== radioDetailsState.length - 1 && (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                              />
                            </TableRow>
                          )}
                        </>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={2}>
                          No Radioisotopes/Test Kits Specified
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Declaration Section */}
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 2, mb: 2, mt: 4, textAlign: "center" }}
          >
            DECLARATION
          </Typography>

          {/* Terms & Conditions Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Terms & Conditions
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
          >
            The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
          >
            The Conformity Assessment Body {"{"}Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP){"}"} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
          >
            By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, lineHeight: 1.6, textAlign: "justify" }}
          >
            Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.
          </Typography>

          {/* Declaration by the Laboratory Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Declaration by the Laboratory
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            We declare that:
          </Typography>

          <Box component="ol" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We have declared all sources of samples.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We have declared and submitted accurate and complete information in the enclosed "Declaration Form"
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We comply to all national, regional and local regulatory requirements for operating a laboratory.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              All information provided in this application is true.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO 15189.
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}>
              {labDetail.DeclNameOrg || 'The authorized representative of CAB'}, authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}>
              I have read and understood all the terms and conditions mentioned above. *
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}>
              By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'. *
            </Typography>
          </Box>
        </div>
      </Box>
    )

  );
};

export default MedicalPreviewReport;
